import { env } from '@/lib/env';

/**
 * The fixed error shape every Worker endpoint returns.
 * docs/ARCHITECTURE.md §2.8.
 */
export type ApiErrorCode =
  | 'UNAUTHENTICATED'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'VALIDATION_ERROR'
  | 'BOT_UNREACHABLE'
  | 'BOT_DEGRADED'
  | 'RATE_LIMITED'
  | 'INTERNAL_ERROR'
  | 'NETWORK_ERROR';

export class ApiError extends Error {
  code: ApiErrorCode;
  details?: unknown;

  constructor(code: ApiErrorCode, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.details = details;
  }
}

interface ApiSuccessEnvelope<T> {
  data: T;
}

interface ApiErrorEnvelope {
  error: {
    code: ApiErrorCode;
    message: string;
    details?: unknown;
  };
}

/**
 * The single shared fetch wrapper every API-client module goes through.
 * No feature component calls fetch() directly — see ARCHITECTURE.md §1.6.
 *
 * `credentials: 'include'` is required, not optional: the frontend
 * (Cloudflare Pages) and Worker are different origins in the real
 * deployment target, and the browser will not send the session cookie
 * cross-origin without this — a silent, easy-to-miss bug that would have
 * broken auth entirely despite every other piece being correct.
 *
 * Deliberately does NOT auto-redirect to /login on a 401 here — that
 * decision belongs to whichever caller actually cares (AuthProvider, on
 * its own /me check), not to every single API call indiscriminately. A
 * background permission check failing shouldn't yank the user off
 * whatever page they're reading.
 */
export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;

  try {
    response = await fetch(`${env.apiBaseUrl}${path}`, {
      ...init,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        ...init?.headers,
      },
    });
  } catch (cause) {
    throw new ApiError(
      'NETWORK_ERROR',
      'Could not reach the Dem Web API. Check your connection and try again.',
      cause,
    );
  }

  let body: unknown;
  try {
    body = await response.json();
  } catch {
    body = undefined;
  }

  if (!response.ok) {
    const errorBody = body as ApiErrorEnvelope | undefined;
    if (errorBody?.error) {
      throw new ApiError(errorBody.error.code, errorBody.error.message, errorBody.error.details);
    }
    throw new ApiError(
      'INTERNAL_ERROR',
      `Request failed with status ${response.status}.`,
      body,
    );
  }

  return (body as ApiSuccessEnvelope<T>).data;
}
